import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { setAnalyticsUser, clearAnalyticsUser } from '@/lib/analytics';

export type DeletionStatus = 'active' | 'pending' | 'deleted';

interface DeletionInfo {
  status: DeletionStatus;
  scheduledFor: string | null;
  requestedAt: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  emailVerified: boolean;
  deletionInfo: DeletionInfo | null;
  signOut: () => Promise<void>;
  refreshDeletionStatus: () => Promise<void>;
  refreshEmailVerified: () => Promise<void>;
}

const defaultAuthContext: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  emailVerified: false,
  deletionInfo: null,
  signOut: async () => undefined,
  refreshDeletionStatus: async () => undefined,
  refreshEmailVerified: async () => undefined,
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState<DeletionInfo | null>(null);
  const requestIdRef = useRef(0);

  const clearDerivedState = useCallback(() => {
    setDeletionInfo(null);
    setEmailVerified(false);
  }, []);

  const fetchProfileInfo = useCallback(async (authUser: User, requestId: number) => {
    const isGoogleUser = authUser.app_metadata?.provider === 'google';

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('deletion_status, deletion_scheduled_for, data_deletion_requested_at, email_verified, full_name, phone, address')
        .eq('user_id', authUser.id)
        .maybeSingle();

      if (requestIdRef.current !== requestId) return;

      if (error) {
        console.error('Failed to fetch profile info:', error);
        setDeletionInfo(null);
        setEmailVerified(isGoogleUser);
        return;
      }

      if (!data) {
        setDeletionInfo(null);
        setEmailVerified(isGoogleUser);
        return;
      }

      const profile = data as Record<string, unknown>;
      setDeletionInfo({
        status: (profile.deletion_status as DeletionStatus) || 'active',
        scheduledFor: (profile.deletion_scheduled_for as string) || null,
        requestedAt: (profile.data_deletion_requested_at as string) || null,
      });
      setEmailVerified(isGoogleUser || ((profile.email_verified as boolean) ?? false));

      // Enrich analytics with full profile data
      setAnalyticsUser({
        userId: authUser.id,
        email: authUser.email,
        phone: (profile.phone as string) || authUser.phone,
        fullName: (profile.full_name as string) || undefined,
        address: (profile.address as string) || undefined,
      });
    } catch (error) {
      if (requestIdRef.current !== requestId) return;
      console.error('Error fetching profile info:', error);
      setDeletionInfo(null);
      setEmailVerified(isGoogleUser);
    }
  }, []);

  const syncSession = useCallback(async (
    nextSession: Session | null,
    options: { hydrateProfile?: boolean } = {},
  ) => {
    const requestId = ++requestIdRef.current;

    setSession(nextSession);
    setUser(nextSession?.user ?? null);

    if (!nextSession?.user) {
      clearDerivedState();
      clearAnalyticsUser();
      return;
    }

    // Enrich analytics with authenticated user data
    setAnalyticsUser({
      userId: nextSession.user.id,
      email: nextSession.user.email,
      phone: nextSession.user.phone,
    });

    if (!options.hydrateProfile) return;

    await fetchProfileInfo(nextSession.user, requestId);
  }, [clearDerivedState, fetchProfileInfo]);

  const refreshDeletionStatus = async () => {
    if (!user) return;
    await fetchProfileInfo(user, requestIdRef.current);
  };

  const refreshEmailVerified = async () => {
    if (!user) return;

    if (user.app_metadata?.provider === 'google') {
      setEmailVerified(true);
      return;
    }

    const { data } = await supabase
      .from('profiles')
      .select('email_verified')
      .eq('user_id', user.id)
      .maybeSingle();

    setEmailVerified(((data as Record<string, unknown>)?.email_verified as boolean) ?? false);
  };

  useEffect(() => {
    let isMounted = true;
    let isAuthReady = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isMounted || !isAuthReady) return;
      void syncSession(nextSession, { hydrateProfile: true });
    });

    void (async () => {
      try {
        const { data: { session: restoredSession } } = await supabase.auth.getSession();

        if (!isMounted) return;

        let validatedSession: Session | null = restoredSession;

        if (restoredSession?.access_token) {
          const { data, error } = await supabase.auth.getUser(restoredSession.access_token);

          if (!isMounted) return;

          if (error || !data.user) {
            validatedSession = null;
          } else {
            validatedSession = {
              ...restoredSession,
              user: data.user,
            };
          }
        }

        await syncSession(validatedSession, { hydrateProfile: true });
      } finally {
        if (!isMounted) return;
        isAuthReady = true;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      requestIdRef.current += 1;
      subscription.unsubscribe();
    };
  }, [syncSession]);

  const signOut = async () => {
    requestIdRef.current += 1;
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    clearDerivedState();
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      emailVerified,
      deletionInfo,
      signOut,
      refreshDeletionStatus,
      refreshEmailVerified,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
